/** @jsxImportSource @emotion/react */

import React, { ReactNode } from 'react';
import { useTheme } from '../../ThemeProvider/ThemeProvider';
import { LinearLayout } from '../LinearLayout/LinearLayout';
import { Text } from '../Text/Text';
import { CSSProp } from '../../../types/emotion';

export interface ChatMessageProps {
  /**
   * Variant of the chat message
   */
  variant?: 'userInput' | 'response';
  /**
   * Initials of the user
   */
  initials?: string;
  /**
   * Path to the response image
   */
  responseImage?: string;
  /**
   * Whether to show action items
   */
  showActionItems?: boolean;
  /**
   * Action items
   */
  actionItems?: ReactNode;
  /**
   * Content of message
   */
  children?: ReactNode;
}

export function ChatMessage({
  variant = 'userInput',
  initials,
  responseImage,
  showActionItems,
  actionItems,
  children,
  ...props
}: ChatMessageProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof ChatMessageProps> & CSSProp) {
  const { tokens } = useTheme();

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'start',
        '&:hover': { 'div > .actionItems': { visibility: 'visible' } },
      }}
      {...props}
    >
      {variant === 'userInput' ? (
        <Text
          css={{ background: tokens.colors['secondary-base-100'], padding: '1rem', width: '3.5rem', height: '3.5rem' }}
        >
          {initials}
        </Text>
      ) : (
        <img
          src={responseImage}
          css={{ padding: '1rem', width: '3.5rem', height: '3.5rem', objectFit: 'contain' }}
          alt=""
        />
      )}
      <LinearLayout
        spacing="tiny"
        css={{ marginLeft: '1rem', overflow: 'hidden', width: variant === 'response' ? '100%' : undefined }}
      >
        {variant === 'response' ? (
          <div
            css={{
              marginLeft: '1rem',
              padding: '1rem',
              width: 'calc(100% - 5rem)',
              position: 'relative',
              display: 'flex',
              gap: '0.25rem',
              alignItems: 'end',
              overflowX: 'clip',
              background: tokens.colors['secondary-base-100'],
            }}
            data-testid="message-ai-response"
          >
            <div css={{ flex: 1, overflow: 'hidden' }}>{children}</div>
          </div>
        ) : (
          children
        )}
        <>
          <LinearLayout
            orientation="horizontal"
            className="actionItems"
            spacing="small"
            align="center"
            css={{
              height: '1.5rem',
              marginLeft: '0.5rem',
              visibility: showActionItems ? 'visible' : 'hidden',
            }}
          >
            {actionItems}
          </LinearLayout>
        </>
      </LinearLayout>
    </div>
  );
}
